/** What the supplier screens call things - **one home** for the words (design principle 7).
 *
 * Separate from the components so a fast-refresh boundary stays a boundary, and so the exchange
 * screen, the account page and the form cannot name one state two ways.
 */

import type { AllocationBasisName, Exchange } from "../api/suppliers";

const STATE_LABEL: Record<string, string> = {
  open: "Waiting for an answer",
  answered: "Answered",
  accepted: "In the inventory",
  declined: "Declined",
  withdrawn: "Withdrawn",
};

/** What each allocation basis is called to a supplier. **Exhaustive by type**: a `Record` over the
 * generated union, so a basis added on the server fails the typecheck here instead of appearing as
 * a raw enum value in a select. The hint says what the *whole* of that basis is, because the form
 * asks for the buyer's part and the whole, and "the whole of tonne-km" is not self-evident. */
export const BASIS_LABEL: Record<AllocationBasisName, { label: string; whole: string }> = {
  tonne_km: { label: "Tonne-kilometres", whole: "all tonne-kilometres you carried or delivered" },
  spend: { label: "Spend", whole: "everything every customer spent with you" },
  mass: { label: "Mass delivered", whole: "the total mass you delivered" },
  units: { label: "Units or deliveries", whole: "the total count of units or deliveries" },
  energy: { label: "Energy", whole: "the total energy delivered or consumed" },
  floor_area: { label: "Floor area", whole: "the total floor area of the site" },
  headcount: { label: "Headcount", whole: "the total number of people" },
};

export function stateLabel(state: string): string {
  return STATE_LABEL[state] ?? state;
}

/** The suppliers this workspace has asked before, in the order the exchange lists them, each once.
 * Only rows where this workspace is the buyer: on the other side the counterparty is a customer. */
export function knownSuppliers(rows: readonly Exchange[]): Array<{ id: string; asked: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.i_am !== "buyer") continue;
    counts.set(row.supplier_org_id, (counts.get(row.supplier_org_id) ?? 0) + 1);
  }
  return [...counts].map(([id, asked]) => ({ id, asked }));
}

/** A workspace id short enough for a select and long enough to tell two apart: the tail, because a
 * UUIDv7's head is a timestamp and two workspaces made together share it. The account page prints
 * the whole id. One home, so the picker that asks a supplier and the picker that permits a customer
 * name a workspace the same way. */
export function workspaceTail(id: string): string {
  return `…${id.slice(-8)}`;
}
